import React, { useState } from "react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { getPasskey, isFramed, passkeysSupported, mfaErr } from "../../lib/webauthn";
import IframeBanner from "./IframeBanner";

const field = "min-h-[44px] w-full rounded border border-erf-borderCream bg-white px-3 text-base tracking-widest focus:outline-none focus:ring-2 focus:ring-erf-gold";

// Second sign-in step. The password step returned {mfa_token, methods}; no session exists until this succeeds.
export default function MfaChallenge({ pending, onDone, onCancel }) {
  const { completeMfa } = useAuth();
  const [method, setMethod] = useState(pending.methods.includes("passkey") && passkeysSupported() && !isFramed() ? "passkey" : pending.methods.find((x) => x !== "passkey"));
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const base = { mfa_token: pending.mfa_token, remember_device: remember, remember_days: 30 };

  const run = async (fn) => {
    setBusy(true); setErr("");
    try { onDone(await fn()); } catch (e) { setErr(mfaErr(e)); } finally { setBusy(false); }
  };
  const passkey = () => run(async () => {
    const o = (await api.post("/auth/mfa/passkey/options", { mfa_token: pending.mfa_token })).data;
    return completeMfa({ ...base, method: "passkey", challenge_id: o.challenge_id, credential: await getPasskey(o.options) });
  });
  const submitCode = (e) => { e.preventDefault(); run(() => completeMfa({ ...base, method, code })); };

  return (
    <main className="min-h-screen bg-erf-cream flex items-center justify-center p-4" data-testid="mfa-challenge">
      <section className="w-full max-w-[420px] bg-white border border-erf-borderCream rounded-md p-5 space-y-4" aria-labelledby="mfa-h">
        <h1 id="mfa-h" className="font-nav text-lg font-semibold text-erf-dark">Confirm it's you</h1>
        <IframeBanner />
        {err && <p role="alert" className="text-sm text-red-700" data-testid="mfa-challenge-error">{err}</p>}
        {method === "passkey" ? (
          <Button type="button" disabled={busy} onClick={passkey} className="w-full min-h-[44px] bg-erf-dark text-white" data-testid="mfa-challenge-passkey">
            Use Face ID / passkey
          </Button>
        ) : (
          <form onSubmit={submitCode} className="space-y-3">
            <label htmlFor="mfa-code" className="block text-xs font-nav text-erf-dark/80">
              {method === "totp" ? "6-digit code from your authenticator app" : "One of your recovery codes"}
            </label>
            <input id="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} className={field} autoComplete="one-time-code"
              inputMode={method === "totp" ? "numeric" : "text"} data-testid="mfa-challenge-code" />
            <Button type="submit" disabled={busy || code.trim().length < 6} className="w-full min-h-[44px] bg-erf-dark text-white" data-testid="mfa-challenge-submit">Continue</Button>
          </form>
        )}
        <label className="flex items-center gap-2 min-h-[44px] text-sm cursor-pointer">
          <input type="checkbox" className="w-5 h-5 accent-erf-gold" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="mfa-challenge-remember" />
          Remember this device for 30 days
        </label>
        <div className="flex flex-wrap gap-2" data-testid="mfa-challenge-methods">
          {pending.methods.filter((x) => x !== method && (x !== "passkey" || (passkeysSupported() && !isFramed()))).map((x) => (
            <button key={x} type="button" onClick={() => { setMethod(x); setCode(""); setErr(""); }}
              className="min-h-[44px] px-3 text-xs underline rounded focus:outline-none focus:ring-2 focus:ring-erf-gold" data-testid={`mfa-challenge-use-${x}`}>
              {x === "passkey" ? "Use a passkey" : x === "totp" ? "Use authenticator app" : "Use a recovery code"}
            </button>
          ))}
          <button type="button" onClick={onCancel} className="min-h-[44px] px-3 text-xs rounded focus:outline-none focus:ring-2 focus:ring-erf-gold" data-testid="mfa-challenge-cancel">Back to sign in</button>
        </div>
      </section>
    </main>
  );
}
